/* SPEC-463 — the right context panel (the "One True SaaS Layout", part 2).
 *
 * ONE collapsible panel on the right of the page that answers "what does this mean, why is
 * it in this state, and what is the detail on the thing I selected". It replaces the
 * one-off right panels: the observability stage panel (NodePanel, SPEC-448) and the Box A
 * amendment panel (SPEC-454) now MOUNT here through `ContextPanelSlot` — same components,
 * new mount.
 *
 * THE CONTRACT (frontend/src/CONTEXT.md)
 *  - A page OPTS IN with `useContextPanelPage({ emptyHint })`. A page that does not opt in
 *    gets no panel and loses no width.
 *  - Each section renders ONLY with real content; with none at all, the panel says the
 *    page's one-line hint. Never an empty card.
 *      (a) the selected thing      — `useContextPanel().setEntity`
 *      (b) why a figure shows a state — `setStates`: the general meaning from the ONE
 *          glossary (SPEC-453) plus the cause the run RECORDED; never an invented cause
 *      (c) the page's measures, defined — `glossaryTerms`; a term the glossary does not
 *          define is dropped, not shown blank
 *      (d) the Decisions Desk card for this page — NOT RENDERED: decision cards carry no
 *          page or entity hint today (`/api/rulings/pending` serves none), so there is
 *          nothing honest to match on. The ask is in the SPEC-463 brief.
 *      (e) the page's own slot     — `<ContextPanelSlot>`
 *      (f) COPILOT — RESERVED. The chatbot revisit lands here; `ReservedCopilotSlot` is the
 *          typed seam and renders nothing.
 *  - It never squeezes the page below `MIN_MAIN_WIDTH` (`config/contextPanel`). Without
 *    that room — a phone, a narrow window, the chat drawer open — it does not dock, and a
 *    `ContextPanelSlot` renders its content INLINE where the page placed it. Nothing a page
 *    puts in the slot is ever unreachable.
 *  - Open/closed is remembered per page (localStorage). Dense table pages start closed.
 *    A closed panel keeps its body mounted (hidden), so slot content keeps its state.
 *
 * A11Y: `]` toggles (never while typing); focus moves into the panel when the reader opens
 * it and returns to where it was on close; Escape inside the panel closes it. Buttons are
 * 32px squares with an aria-label and a visible focus ring. Colours are tokens, never
 * alpha-on-text (check-opacity-text.mjs). No width animation, so there is nothing for
 * prefers-reduced-motion to suppress. */
import React, {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { PanelRightOpen, PanelRightClose } from 'lucide-react';
import { useGlossary, lookupTerm } from '@/hooks/useGlossary';
import { HonestState } from '@/components/promo/HonestState';
import type { HonestStateStr } from '@/lib/promoTypes';
import {
  canDock, isToggleShortcut, pageKey, parseStored, resolveOpen,
  PANEL_WIDTH, STRIP_WIDTH, STORAGE_KEY,
} from '@/config/contextPanel';

/* ── Types ──────────────────────────────────────────────────────────────────────── */

/** (a) The thing the reader selected. Every value is a string the page already shows. */
export interface ContextEntity {
  /** What kind of thing this is, in planner words ("Selected line"). */
  kind: string;
  title: string;
  facts?: Array<{ label: string; value: string }>;
}

/** (b) One figure that shows a state instead of a number. `detail` is the cause the RUN
 *  recorded for it, or null — never a cause composed on screen. */
export interface ContextStateNote {
  figure: string;
  state: HonestStateStr;
  detail?: string | null;
}

/** (f) RESERVED for the copilot (the chatbot revisit). Typed as `null` so nothing can
 *  render into it until that spec gives it a shape. */
export type ReservedCopilotSlot = null;

interface PageRegistration {
  /** One line shown when no section has content: what to select to fill the panel. */
  emptyHint: string;
  /** (c) The page's hero measures. Undefined terms are dropped. */
  glossaryTerms?: string[];
}

interface ContextPanelValue {
  registration: PageRegistration | null;
  /** Mount/unmount only. Returns the unregister cleanup. */
  register: () => () => void;
  /** Update the registration's fields without clearing the page's entity/states. */
  updateRegistration: (r: PageRegistration) => void;
  entity: ContextEntity | null;
  setEntity: (e: ContextEntity | null) => void;
  states: ContextStateNote[];
  setStates: (s: ContextStateNote[]) => void;
  /** The slot's DOM target while the panel is docked; null otherwise. */
  slotEl: HTMLElement | null;
  setSlotEl: (el: HTMLElement | null) => void;
  slotUsers: number;
  addSlotUser: () => () => void;
  docked: boolean;
  setRowWidth: (w: number) => void;
  /** True while a page has suspended the panel (the full-screen map overlay). */
  suspended: boolean;
  setSuspended: (on: boolean) => void;
  open: boolean;
  /** User-initiated toggle; moves focus into the panel / back out. */
  toggle: () => void;
  /** Open without moving focus — the reader just selected something to read about. */
  reveal: () => void;
  copilot: ReservedCopilotSlot;
}

const Ctx = createContext<ContextPanelValue | null>(null);

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function readStored(): Record<string, boolean> {
  try {
    return parseStored(localStorage.getItem(STORAGE_KEY));
  } catch {
    return {};
  }
}

/* ── Provider ───────────────────────────────────────────────────────────────────── */

export function ContextPanelProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const [registration, setRegistration] = useState<PageRegistration | null>(null);
  const regToken = useRef(0);
  const [entity, setEntityState] = useState<ContextEntity | null>(null);
  const [states, setStatesState] = useState<ContextStateNote[]>([]);
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null);
  const [slotUsers, setSlotUsers] = useState(0);
  // Review F3: hold the dock DECISION, not the raw width, so a resize re-renders consumers
  // only when docking actually flips (React bails out on an unchanged boolean).
  const [roomToDock, setRoomToDock] = useState(false);
  const setRowWidth = useCallback((w: number) => setRoomToDock(canDock(w)), []);
  const [suspended, setSuspended] = useState(false);
  const [stored, setStored] = useState<Record<string, boolean>>(readStored);

  const register = useCallback(() => {
    const token = ++regToken.current;
    return () => {
      // Only the LATEST registrant clears: a page that unmounts after the next one
      // registered must not wipe the new page's panel.
      if (regToken.current !== token) return;
      setRegistration(null);
      setEntityState(null);
      setStatesState([]);
    };
  }, []);

  const updateRegistration = useCallback((r: PageRegistration) => {
    setRegistration((prev) => (sameJson(prev, r) ? prev : r));
  }, []);

  const setEntity = useCallback((e: ContextEntity | null) => {
    setEntityState((prev) => (sameJson(prev, e) ? prev : e));
  }, []);
  const setStates = useCallback((s: ContextStateNote[]) => {
    setStatesState((prev) => (sameJson(prev, s) ? prev : s));
  }, []);
  const addSlotUser = useCallback(() => {
    setSlotUsers((n) => n + 1);
    return () => setSlotUsers((n) => n - 1);
  }, []);

  const key = pageKey(pathname);
  const open = resolveOpen(stored, pathname);
  const docked = registration !== null && roomToDock;

  // Review F6: the storage write sits OUTSIDE the state updater (updaters must be pure),
  // and re-reads storage first so a second tab's choices for other pages are not dropped.
  const persist = useCallback((next: boolean) => {
    const map = { ...readStored(), [key]: next };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
      /* storage unavailable — the choice holds for this session only */
    }
    setStored(map);
  }, [key]);

  // Focus intent for the NEXT open/close render: set only by a user toggle, so a page
  // that loads with the panel open never steals focus.
  const focusIntent = useRef<'into' | 'back' | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const toggle = useCallback(() => {
    if (!open) {
      returnTo.current = document.activeElement as HTMLElement | null;
      focusIntent.current = 'into';
    } else {
      // Closed from the page (the shortcut): focus stays where the reader is. Closed from
      // inside the panel: focus returns to where it was when the panel opened.
      const active = document.activeElement as HTMLElement | null;
      const panel = document.getElementById(PANEL_ID);
      if (active && active !== document.body && !(panel && panel.contains(active))) {
        returnTo.current = active;
      }
      focusIntent.current = 'back';
    }
    persist(!open);
  }, [open, persist]);
  const reveal = useCallback(() => {
    if (!open && !suspended) persist(true);
  }, [open, suspended, persist]);

  // Review F3: one stable object per real change, not per provider render.
  const value = useMemo<ContextPanelValue>(() => ({
    registration, register, updateRegistration, entity, setEntity, states, setStates,
    slotEl, setSlotEl, slotUsers, addSlotUser,
    docked, setRowWidth, suspended, setSuspended, open, toggle, reveal, copilot: null,
  }), [registration, register, updateRegistration, entity, setEntity, states, setStates,
    slotEl, slotUsers, addSlotUser, docked, setRowWidth, suspended, open, toggle, reveal]);
  return (
    <Ctx.Provider value={value}>
      <FocusBridge focusIntent={focusIntent} returnTo={returnTo} open={open} />
      {children}
    </Ctx.Provider>
  );
}

/** Applies the focus intent after the open/close render has committed. */
function FocusBridge({ focusIntent, returnTo, open }: {
  focusIntent: React.MutableRefObject<'into' | 'back' | null>;
  returnTo: React.MutableRefObject<HTMLElement | null>;
  open: boolean;
}) {
  useEffect(() => {
    const intent = focusIntent.current;
    focusIntent.current = null;
    if (intent === 'into' && open) {
      document.getElementById(CLOSE_BUTTON_ID)?.focus();
    } else if (intent === 'back' && !open) {
      const target = returnTo.current;
      const panel = document.getElementById(PANEL_ID);
      if (target && target.isConnected && !(panel && panel.contains(target))) target.focus();
      else document.getElementById(OPEN_BUTTON_ID)?.focus();
      returnTo.current = null;
    }
  }, [open, focusIntent, returnTo]);
  return null;
}

/* ── Page-facing API ────────────────────────────────────────────────────────────── */

const NOOP_VALUE = {
  setEntity: (_e: ContextEntity | null) => {},
  setStates: (_s: ContextStateNote[]) => {},
  reveal: () => {},
  setSuspended: (_on: boolean) => {},
  docked: false,
};

/** What a page uses. Safe outside the shell (tests, the stats shell): every call is a
 *  no-op and `docked` is false, so slot content renders inline. */
export function useContextPanel(): {
  setEntity: (e: ContextEntity | null) => void;
  setStates: (s: ContextStateNote[]) => void;
  reveal: () => void;
  /** Suspend the panel (no `]`, no reveal) while the page covers the shell, e.g. a
   *  full-screen overlay. */
  setSuspended: (on: boolean) => void;
  docked: boolean;
} {
  const ctx = useContext(Ctx);
  if (!ctx) return NOOP_VALUE;
  return {
    setEntity: ctx.setEntity, setStates: ctx.setStates, reveal: ctx.reveal,
    setSuspended: ctx.setSuspended, docked: ctx.docked,
  };
}

/** SPEC-514: the width the docked panel takes at the right edge right now — the open panel,
 *  the closed strip with its open button, or 0 when it does not dock. The top bar reserves
 *  exactly this much so it never covers the panel or its toggle. */
export function useContextPanelWidth(): number {
  const ctx = useContext(Ctx);
  if (!ctx || !ctx.docked) return 0;
  return ctx.open ? PANEL_WIDTH : STRIP_WIDTH;
}

/** Opt this page into the panel while the calling component is mounted. */
export function useContextPanelPage(r: PageRegistration): void {
  const ctx = useContext(Ctx);
  const register = ctx?.register;
  const updateRegistration = ctx?.updateRegistration;
  const termsKey = (r.glossaryTerms || []).join('\u0000');
  // Review F7: mount/unmount and field updates are separate effects, so a changed hint never
  // runs the unmount cleanup that would wipe the page's entity and states.
  useEffect(() => {
    if (!register) return undefined;
    return register();
  }, [register]);
  useEffect(() => {
    updateRegistration?.({ emptyHint: r.emptyHint, glossaryTerms: termsKey ? termsKey.split('\u0000') : undefined });
  }, [updateRegistration, r.emptyHint, termsKey]);
}

/** (e) The page's own content for the panel. Docked → portalled into the panel's slot.
 *  Not docked, no shell, or `inline` → rendered right here, where the page placed it.
 *  Docked but CLOSED → still portalled (so its state survives), plus a one-line notice HERE
 *  with an open button: content the reader folded away is never silently missing (review F2).
 *  `label` names the content in that notice ("The amendment form"). */
export function ContextPanelSlot({ children, inline = false, label }: {
  children: React.ReactNode; inline?: boolean; label: string;
}) {
  const ctx = useContext(Ctx);
  const target = !inline && ctx?.docked ? ctx.slotEl : null;
  const addSlotUser = ctx?.addSlotUser;
  useEffect(() => {
    if (!target || !addSlotUser) return undefined;
    return addSlotUser();
  }, [target, addSlotUser]);
  if (target) {
    return (
      <>
        {createPortal(children, target)}
        {!ctx?.open && (
          <p className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--text-secondary)]">
            <span>{label} is in the side panel, which is folded away.</span>
            <button
              type="button"
              onClick={ctx?.toggle}
              className="inline-flex items-center gap-1 min-h-[28px] px-2 rounded-lg border border-[var(--border-subtle)] text-[var(--text-primary)] hover:border-[var(--border-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60"
            >
              <PanelRightOpen className="w-3.5 h-3.5" aria-hidden="true" />
              Open the side panel
            </button>
          </p>
        )}
      </>
    );
  }
  return <>{children}</>;
}

/* ── Shell-facing pieces ────────────────────────────────────────────────────────── */

const PANEL_ID = 'context-panel';
const BODY_ID = 'context-panel-body';
const CLOSE_BUTTON_ID = 'context-panel-close';
const OPEN_BUTTON_ID = 'context-panel-open';

const BUTTON_CLASS =
  'relative grid place-items-center w-8 h-8 rounded-lg text-[var(--text-tertiary)] ' +
  'hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60';

/** The row the page and the panel share. It measures itself so the panel docks only with
 *  room to spare, and owns the `]` shortcut. */
export function ContextPanelRow({ children }: { children: React.ReactNode }) {
  const ctx = useContext(Ctx);
  const rowRef = useRef<HTMLDivElement>(null);
  const setRowWidth = ctx?.setRowWidth;

  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el || !setRowWidth) return undefined;
    setRowWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setRowWidth(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [setRowWidth]);

  const docked = Boolean(ctx?.docked);
  const toggle = ctx?.toggle;
  const suspended = Boolean(ctx?.suspended);
  useEffect(() => {
    if (!docked || !toggle || suspended) return undefined;
    const onKey = (e: KeyboardEvent) => {
      // Review F4: never when another handler consumed the key, mid-IME composition, on
      // auto-repeat, or while a modal (the full-screen map, the Decisions Desk) is up — the
      // panel is behind it and moving focus there would break the modal's focus trap.
      if (e.defaultPrevented || e.isComposing || e.repeat) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      const t = e.target as HTMLElement | null;
      if (!isToggleShortcut({
        key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey,
        targetTag: t?.tagName ?? null, targetEditable: Boolean(t?.isContentEditable),
      })) return;
      e.preventDefault();
      toggle();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [docked, toggle, suspended]);

  return (
    <div ref={rowRef} className="flex-1 min-h-0 flex">
      {children}
      {docked && <ContextPanelAside />}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
      {children}
    </h3>
  );
}

function ContextPanelAside() {
  const ctx = useContext(Ctx) as ContextPanelValue;
  const index = useGlossary();
  const { registration, entity, states, open, toggle, slotUsers, setSlotEl } = ctx;

  // (c) Only terms the ONE glossary defines; one row per entry even when two keys alias it.
  const glossaryEntries = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<NonNullable<ReturnType<typeof lookupTerm>>> = [];
    for (const t of registration?.glossaryTerms || []) {
      const entry = lookupTerm(index, t);
      if (entry && !seen.has(entry.term)) {
        seen.add(entry.term);
        out.push(entry);
      }
    }
    return out;
  }, [index, registration]);

  // (b) Figures that share a state AND a recorded cause read once, not three times.
  const stateGroups = useMemo(() => {
    const groups = new Map<string, { state: HonestStateStr; detail: string | null; figures: string[] }>();
    for (const s of states) {
      const detail = s.detail && s.detail.trim() ? s.detail.trim() : null;
      const key = `${s.state}\u0000${detail ?? ''}`;
      const g = groups.get(key);
      if (g) g.figures.push(s.figure);
      else groups.set(key, { state: s.state, detail, figures: [s.figure] });
    }
    return Array.from(groups.values());
  }, [states]);

  const hasEntity = Boolean(entity && entity.title);
  const hasStates = stateGroups.length > 0;
  const hasGlossary = glossaryEntries.length > 0;
  const hasSlot = slotUsers > 0;
  const hasContent = hasEntity || hasStates || hasGlossary || hasSlot;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape' || !open || e.defaultPrevented) return;
    const target = e.target as HTMLElement;
    // Review F5: React events bubble through portals — act only on a key pressed in the
    // panel's OWN DOM, never one bubbling up from a popover a slot component portalled out.
    if (!(e.currentTarget as HTMLElement).contains(target)) return;
    // Escape in a field or a nested widget belongs to it, not the panel.
    const tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (target.closest('[role="dialog"],[role="listbox"],[role="menu"],[role="tooltip"]')) return;
    // A modal elsewhere (the tour, the Decisions Desk) owns Escape.
    if (document.querySelector('[aria-modal="true"]')) return;
    e.stopPropagation();
    toggle();
  };

  return (
    <aside
      id={PANEL_ID}
      data-tour="context-panel"
      aria-label="Context for this screen"
      onKeyDown={onKeyDown}
      className="shrink-0 h-full flex border-l border-[var(--border-subtle)] bg-[var(--bg-surface)]"
      style={{ width: open ? PANEL_WIDTH : STRIP_WIDTH }}
    >
      {!open && (
        // Top padding clears the fixed top-right control cluster, as <main> does.
        <div className="w-full flex flex-col items-center pt-14 md:pt-16">
          <button
            id={OPEN_BUTTON_ID}
            type="button"
            onClick={toggle}
            aria-expanded={false}
            aria-controls={BODY_ID}
            aria-label={hasContent
              ? 'Open the context panel — it has detail for this screen (shortcut ])'
              : 'Open the context panel (shortcut ])'}
            title="Open the context panel  ]"
            className={BUTTON_CLASS}
          >
            <PanelRightOpen className="w-4 h-4" aria-hidden="true" />
            {hasContent && (
              <span aria-hidden="true" className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-400" />
            )}
          </button>
        </div>
      )}
      {/* The body stays MOUNTED while closed (hidden): the slot is a portal target, and
          unmounting it would throw away a half-typed amendment. */}
      <div id={BODY_ID} hidden={!open} className="flex-1 min-w-0 overflow-y-auto pt-14 md:pt-16 px-3 pb-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">About this screen</h2>
          <button
            id={CLOSE_BUTTON_ID}
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-controls={BODY_ID}
            aria-label="Close the context panel (shortcut ])"
            title="Close the context panel  ]"
            className={BUTTON_CLASS}
          >
            <PanelRightClose className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-4">
          {/* (a) The selected thing. */}
          {hasEntity && entity && (
            <section className="space-y-1.5">
              <SectionTitle>{entity.kind}</SectionTitle>
              <p className="text-sm font-medium text-[var(--text-primary)] leading-snug">{entity.title}</p>
              {entity.facts && entity.facts.length > 0 && (
                <dl className="space-y-0.5">
                  {entity.facts.map((f) => (
                    <div key={f.label} className="flex justify-between gap-3 text-xs">
                      <dt className="text-[var(--text-tertiary)]">{f.label}</dt>
                      <dd className="text-[var(--text-secondary)] text-right">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          )}

          {/* (b) Why a figure shows a state: the general meaning (glossary, by the exact
              state token) and the cause the run recorded — or an admitted gap. */}
          {hasStates && (
            <section className="space-y-2">
              <SectionTitle>Why these figures show a state</SectionTitle>
              <ul className="space-y-2.5">
                {stateGroups.map((s) => {
                  const meaning = lookupTerm(index, s.state);
                  const detail = s.detail;
                  return (
                    <li key={`${s.state}:${detail ?? ''}`} className="space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs text-[var(--text-secondary)]">{s.figures.join(' · ')}</span>
                        <HonestState state={s.state} size="sm" className="shrink-0" />
                      </div>
                      {meaning && (
                        <p className="text-[11px] text-[var(--text-tertiary)] leading-relaxed">{meaning.definition}</p>
                      )}
                      <p className="text-[11px] text-[var(--text-secondary)] leading-relaxed">
                        {detail ? `Here: ${detail}` : 'No further detail recorded for this figure.'}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* (c) The page's measures, defined by the ONE glossary. */}
          {hasGlossary && (
            <section className="space-y-2">
              <SectionTitle>What the measures mean</SectionTitle>
              <dl className="space-y-2.5">
                {glossaryEntries.map((g) => (
                  <div key={g.term}>
                    <dt className="text-xs font-medium text-[var(--text-primary)]">{g.term}</dt>
                    <dd className="text-[11px] text-[var(--text-secondary)] leading-relaxed mt-0.5">{g.definition}</dd>
                    {g.pending_ruling && (
                      <dd className="text-[10px] text-amber-400 leading-relaxed mt-1">
                        This is how we run it today. It is still open for you to settle.
                      </dd>
                    )}
                  </div>
                ))}
              </dl>
            </section>
          )}

          {/* (d) The Decisions Desk card for this page: NOT RENDERED — the cards carry no
              page/entity hint to match on (see the header comment). */}

          {/* (e) The page's own slot — portal target for <ContextPanelSlot>. */}
          <div ref={setSlotEl} />

          {/* (f) COPILOT — RESERVED (ReservedCopilotSlot). Renders nothing until the chatbot
              revisit gives it content. */}

          {!hasContent && registration && (
            <p className="text-xs text-[var(--text-tertiary)] leading-relaxed">{registration.emptyHint}</p>
          )}
        </div>
      </div>
    </aside>
  );
}
